import React, { useEffect, useState } from 'react';
import { PersonInterface } from '../../Common/types';
import { getTeamExportData } from '../teamApi';
import { MinimalTeam } from '../types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import FormAutocompleteBox from '../../Common/FormItems/FormAutocompleteBox';
import { arrayify, downloadFile } from '../../Common/utilities';
import { theme } from '../../Common/styling';
import { Button, Checkbox, CircularProgress, DialogContentText, FormControl, FormControlLabel } from '@mui/material';
import { CancelOutlined, FileDownloadOutlined } from '@mui/icons-material';

type FormValues = {
  name: boolean;
  email: boolean;
  mobile: boolean;
  teams: MinimalTeam[];
};

function personRow(row: string[], values: FormValues, emails: Set<string>, person: PersonInterface) {
  if (values.name) row.push(!person?.name ? '' : `${person.name}${emails.has(person?.email) ? ' (DUBBLETT)' : ''}`);
  if (values.email) row.push((emails.has(person?.email) ? '' : person?.email) || '');
  if (values.mobile) row.push((emails.has(person?.email) ? '' : person?.mobile) || '');
  if (person?.email) emails.add(person.email);
}

type PersonalDataTabProps = {
  teams?: MinimalTeam[];
  chosenTeams?: MinimalTeam[];
  loadingTeams: boolean;
  loadingChosenTeams: boolean;
  handleClose: () => void;
};

export default function PersonalDataTab({
  teams,
  chosenTeams,
  loadingTeams,
  loadingChosenTeams,
  handleClose,
}: PersonalDataTabProps) {
  const [initialValues, setInitialValues] = useState<FormValues>({
    name: true,
    email: true,
    mobile: true,
    teams: [],
  });

  useEffect(() => {
    setInitialValues({
      name: true,
      email: true,
      mobile: true,
      teams: chosenTeams ?? [],
    });
  }, [chosenTeams]);

  const f = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: handleExport,
    validationSchema: Yup.object({
      teams: Yup.array().min(1, 'Måste välja minst ett team'),
    }),
  });

  async function handleExport(values: FormValues) {
    const exportData = await getTeamExportData(values.teams).then(({ data }) =>
      data.data.map(({ people_attributes, ...team }) => {
        const ambassadors: PersonInterface[] = [];
        const residenceManagers: PersonInterface[] = [];
        people_attributes.forEach((person) => {
          if (person.person_type === 'ambassador') ambassadors.push(person);
          if (person.person_type === 'residence_manager') residenceManagers.push(person);
        });
        return { ...team, ambassadors, residenceManagers };
      })
    );

    const maxResidenceManagers = Math.max(...exportData.map((t) => t.residenceManagers.length));
    const maxAmbassadors = Math.max(...exportData.map((t) => t.ambassadors.length));

    const csvValues = [['Boende']];
    for (let i = 0; i < maxResidenceManagers; i++) {
      if (values.name) csvValues[0].push('VC Namn');
      if (values.email) csvValues[0].push('VC Mejl');
      if (values.mobile) csvValues[0].push('VC Tele');
    }
    for (let i = 0; i < maxAmbassadors; i++) {
      if (values.name) csvValues[0].push('UOA Namn');
      if (values.email) csvValues[0].push('UOA Mejl');
      if (values.mobile) csvValues[0].push('UOA Tele');
    }

    const emails = new Set<string>();
    exportData.forEach((team) => {
      const row = [team.name];
      for (let i = 0; i < maxResidenceManagers; i++) {
        personRow(row, values, emails, team.residenceManagers[i]);
      }
      for (let i = 0; i < maxAmbassadors; i++) {
        personRow(row, values, emails, team.ambassadors[i]);
      }
      csvValues.push(row);
    });

    const blob = new Blob([csvValues.map((row) => row.join(',')).join('\n')], { type: 'text/csv' });
    downloadFile(blob, `Export ${values.teams.length} team ${dayjs().format('YYYY-MM-DD')}.csv`);
  }

  return (
    <>
      <form onSubmit={f.handleSubmit} className="pt-2">
        <div className="h-96">
          <div className="flex gap-2 items-start">
            <FormControl className="grow">
              <FormAutocompleteBox
                type="multiple"
                label="Välj team"
                options={teams ?? []}
                values={f.values.teams ?? []}
                onChange={(_, values) => f.setFieldValue('teams', arrayify(values))}
                renderTags="simple"
                loading={loadingTeams || loadingChosenTeams}
                error={(f.errors.teams as string) ?? null}
              />
            </FormControl>
            <Button variant="outlined" onClick={teams && (() => f.setFieldValue('teams', teams))} className="h-[53px]">
              Välj alla
            </Button>
          </div>

          <div className="w-full mt-4 text-neutral-600">Export av personuppgifter utifrån de valda boenden.</div>

          <div className="w-full mt-8 text-neutral-600">
            Vilka personuppgifter vill du exportera för VC och UOA på de valda teamen?
          </div>

          <div className="flex gap-12 px-2 mt-2">
            <FormControlLabel
              control={
                <Checkbox
                  name="name"
                  // Change with MUI5 update
                  style={f.values.name ? { color: theme.green, borderColor: theme.green } : undefined}
                  // color="secondary"
                  checked={f.values.name}
                  onChange={f.handleChange}
                />
              }
              label="Namn"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="email"
                  // Change with MUI5 update
                  style={f.values.email ? { color: theme.green, borderColor: theme.green } : undefined}
                  // color="secondary"
                  checked={f.values.email}
                  onChange={f.handleChange}
                />
              }
              label="Mejladress"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="mobile"
                  // Change with MUI5 update
                  style={f.values.mobile ? { color: theme.green, borderColor: theme.green } : undefined}
                  // color="secondary"
                  checked={f.values.mobile}
                  onChange={f.handleChange}
                />
              }
              label="Telefonnummer"
            />
          </div>
        </div>

        <hr className="my-4 h-0.5 border-t-0 bg-neutral-100" />

        <div className="flex justify-between mt-10 h-12">
          <div className="flex w-60">
            <Button fullWidth variant="outlined" onClick={handleClose} color="error" endIcon={<CancelOutlined />}>
              Stäng
            </Button>
          </div>

          <div className="flex w-60">
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              disabled={f.isSubmitting || !f.isValid}
              endIcon={f.isSubmitting ? <CircularProgress size="20px" /> : <FileDownloadOutlined />}
            >
              Skapa exportfil
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
